import { Module } from 'vuex';
import axios from 'axios';

import { handleError } from '@/utils/helperFunctions';
import { endpoints } from '@/endpoints/endpoints';
import {
  DueDiligenceState,
  DueDiligenceReminder,
  DueDiligenceDetails,
  PlannedInvestmentVolume,
  DueDiligenceFormStage,
  DueDiligenceSourceOfWealth,
  SourceType,
  DueDiligenceMetadata,
  CountryInformation,
  DueDiligenceClient,
  DueDiligenceRecord,
  DueDiligenceDocumentType,
  DueDiligenceHolderSection,
  PlannedInvestmentFrequency,
} from '@/store/dueDiligence/types';
import { AccountOpeningMetadataData } from '@/endpoints/types';
import { toDate } from '@/utils/helperFunctions';
import {
  DueDiligenceFormStages,
  DueDiligenceStageOrder,
  InitialDetailsState,
  PlannedInvestmentFrequency as PlannedInvestmentFrequencyConstants,
} from './constants';
import { JointAccountType } from '@/types/jointAccountType';
import { USER_ROLES } from '@/utils/roles';
import { number } from '@hapi/joi';
import { AccountReasonNoTinOptions } from '@/utils/accountTypes';

const setStatusText = (status : number) => {
  switch (status) {
    case 1:
      return 'In Progress';
    case 2:
      return 'Completed';
    case 3:
      return 'Overdue';
    default:
      return 'Overdue';
  }
}

const reasonNoTinToString = (reason: unknown): string | null => {
  if (reason == null || reason === '') {
    return null;
  }

  if (typeof reason === 'object' && 'value' in (reason as object)) {
    const value = (reason as { value?: unknown }).value;
    return value == null || value === '' ? null : String(value);
  }

  return String(reason);
};

const withNormalizedTaxReasons = (
  details: DueDiligenceDetails,
): DueDiligenceDetails => {
  const payload: DueDiligenceDetails = JSON.parse(JSON.stringify(details));
  const holders = [
    payload?.GeneralDetails?.PrimaryAccountHolder,
    payload?.GeneralDetails?.SecondaryAccountHolder,
  ];

  holders.forEach((holder) => {
    if (holder?.TaxInfo) {
      holder.TaxInfo.ReasonNoTinNumber = reasonNoTinToString(
        holder.TaxInfo.ReasonNoTinNumber,
      );
    }
  });

  return payload;
};

export const dueDiligence: Module<DueDiligenceState, any> = {
  namespaced: true,
  state: {
    reminder: {
      IsReminderDue: false,
      SkipCount: 0,
      LastDueDiligenceUpdateDate: null,
    },
    details: InitialDetailsState,
    metadata: {
      Options: [],
      CountryInformation: [],
    },
    clients: [],
    dueDiligenceId: null,
    riskCategory: null,
    clientId: null,
    clientBostonId: null,
    dueDiligenceDetails: null,
    DueDiligenceDocumentTypes: [],
    openReminderModal: false,
    openClientModal: false,
    openReminderOverviewModal: false,
    openDocumentOverviewModal: false,
    openConfirmationModal: false,
    openIntroductionModal: true,
    isLoading: false,
    formStage: DueDiligenceFormStages.GeneralDetails,
    isViewOnly: true,
    fiscalResidenceChanged: {
      PrimaryAccountHolder: { changed: false, previousCountry: null, newCountry: null },
      SecondaryAccountHolder: { changed: false, previousCountry: null, newCountry: null },
    },
    dueDiligenceList: [],
  },
  getters: {
    rawCountries(state): AccountOpeningMetadataData[] {
      return state.metadata
        ? state.metadata.Options.filter(
            (o: AccountOpeningMetadataData) => o.Field === 'Country',
          )
        : [];
    },
    sourceOfFundOptions(state): AccountOpeningMetadataData[] {
      return state.metadata
        ? state.metadata.Options.filter(
            (o: AccountOpeningMetadataData) =>
              o.Field === 'Source Of Fund - Other' ||
              o.Field === 'Source of Fund Income Sources',
          ).sort((a, b) => {
            if (
              a.Field === 'Source of Fund Income Sources' &&
              b.Field !== 'Source of Fund Income Sources'
            )
              return -1;
            if (
              a.Field !== 'Source of Fund Income Sources' &&
              b.Field === 'Source of Fund Income Sources'
            )
              return 1;
            return 0;
          })
        : [];
    },
    isLastStage: (state) => {
      const idx = DueDiligenceStageOrder.indexOf(state.formStage);
      return idx === DueDiligenceStageOrder.length - 1;
    },
    isFirstStage: (state) => {
      return DueDiligenceStageOrder.indexOf(state.formStage) === 0;
    },
    primaryAccountMobileNo: (state): string | null => {
      return state.details.GeneralDetails.PrimaryAccountHolder.ContactDetails
        .MobileNo;
    },
    secondaryAccountMobileNo: (state): string | null => {
      return (
        state.details.GeneralDetails.SecondaryAccountHolder?.ContactDetails
          .MobileNo ?? null
      );
    },
    getCountryInfoById:
      (state) =>
      (id: number | null): CountryInformation | null => {
        return (
          state.metadata.CountryInformation.find(
            (country) => country.Id === id,
          ) ?? null
        );
      },
    getAccountHolderFiscalResidence:
      (state) =>
      (type: JointAccountType): number | null => {
        return (
          state.details.GeneralDetails[type]?.TaxInfo
            .CountryOfFiscalResidence ?? null
        );
      },
    getClients:
      (state) =>
      (id: number): DueDiligenceClient[] => {
        return state.clients.filter(
          (client) =>
            client.riskCategory === id &&
            (client.daysOverdue > 0 || !client.dueDiligenceId),
        );
      },
    getCurrentDueDiligence: (state): DueDiligenceRecord | null => {
      return state.dueDiligenceDetails;
    },
    getDocumentTypes: (state): DueDiligenceDocumentType[] => {
      return state.DueDiligenceDocumentTypes;
    },
    getRiskCategory: (state): number | null => {
      return state.riskCategory;
    },
  },
  mutations: {
    setReminderData(state, data: DueDiligenceReminder) {
      state.reminder = data;
    },
    setClients(state, clients: DueDiligenceClient[]) {
      state.clients = clients;
    },
    toggleReminderModal(state) {
      state.openReminderModal = !state.openReminderModal;
    },
    toggleClientModal(state) {
      state.openClientModal = !state.openClientModal;
    },
    toggleReminderOverviewModal(state) {
      state.openReminderOverviewModal = !state.openReminderOverviewModal;
    },
    toggleDocumentOverviewModal(state) {
      state.openDocumentOverviewModal = !state.openDocumentOverviewModal;
    },
    toggleConfirmationModal(state) {
      state.openConfirmationModal = !state.openConfirmationModal;
    },
    toggleIntroductionModal(state) {
      state.openIntroductionModal = !state.openIntroductionModal;
    },
    closeAllModals(state) {
      state.openReminderModal = false;
      state.openConfirmationModal = false;
      state.openIntroductionModal = false;
      state.openReminderOverviewModal = false;
      state.openDocumentOverviewModal = false;
      state.openClientModal = false;
    },
    setDueDiligenceDetails(state, data: DueDiligenceDetails) {
      const generalDetails = data.GeneralDetails as DueDiligenceDetails['GeneralDetails'] & {
        PlannedInvestmentVolume?: PlannedInvestmentVolume | null;
        PlannedInvestmentFrequency?: PlannedInvestmentFrequency | null;
        PlannedInvestmentFrequencyOther?: string | null;
      };
      generalDetails.PlannedInvestmentDetails =
        generalDetails.PlannedInvestmentDetails ?? {
          PlannedInvestmentVolume: generalDetails.PlannedInvestmentVolume ?? null,
          PlannedInvestmentFrequency: generalDetails.PlannedInvestmentFrequency ?? null,
          PlannedInvestmentFrequencyOther: generalDetails.PlannedInvestmentFrequencyOther ?? null,
        };
      data.GeneralDetails.PrimaryAccountHolder.PersonalInfo.BirthDate = toDate(
        data.GeneralDetails.PrimaryAccountHolder.PersonalInfo.BirthDate,
      );
      if (data.GeneralDetails.SecondaryAccountHolder)
        data.GeneralDetails.SecondaryAccountHolder.PersonalInfo.BirthDate =
          toDate(
            data.GeneralDetails?.SecondaryAccountHolder?.PersonalInfo
              ?.BirthDate,
          );
      // Saved MoreInformation stays in the DB; the form always starts blank for a new editing session.
      data.MoreInformation = '';
      state.details = data;
      state.fiscalResidenceChanged = {
        PrimaryAccountHolder: { changed: false, previousCountry: null, newCountry: null },
        SecondaryAccountHolder: { changed: false, previousCountry: null, newCountry: null },
      };
    },
    setDueDiligenceMetadata(state, data: DueDiligenceMetadata) {
      state.metadata = data;
    },
    setOddId(state, id: number) {
      state.dueDiligenceId = id;
    },
    setClientId(state, id: number | null) {
      state.clientId = id;
    },
    setClientBostonId(state, id: number | null) {
      state.clientBostonId = id;
    },
    setPlannedInvestmentVolume(state, data: PlannedInvestmentVolume) {
      state.details.GeneralDetails.PlannedInvestmentDetails.PlannedInvestmentVolume = data;
    },
    setPlannedInvestmentFrequency(state, data: PlannedInvestmentFrequency | null) {
      state.details.GeneralDetails.PlannedInvestmentDetails.PlannedInvestmentFrequency = data;
      if (data !== PlannedInvestmentFrequencyConstants.Others) {
        state.details.GeneralDetails.PlannedInvestmentDetails.PlannedInvestmentFrequencyOther = null;
      }
    },
    setPlannedInvestmentFrequencyOther(state, data: string | null) {
      state.details.GeneralDetails.PlannedInvestmentDetails.PlannedInvestmentFrequencyOther = data;
    },
    setFormStage(state, stage: DueDiligenceFormStage) {
      state.formStage = stage;
    },
    SetCurrentDueDiligenceDetails(state, stage: DueDiligenceRecord) {
      state.dueDiligenceDetails = stage;
    },
    setDocumentTypes(state, data: DueDiligenceDocumentType[]) {
      state.DueDiligenceDocumentTypes = data;
    },
    setRiskCategory(state, data: number | null) {
      state.riskCategory = data;
    },
    nextStage(state) {
      const idx = DueDiligenceStageOrder.indexOf(state.formStage);
      if (idx < DueDiligenceStageOrder.length - 1)
        state.formStage = DueDiligenceStageOrder[idx + 1];
    },
    prevStage(state) {
      const idx = DueDiligenceStageOrder.indexOf(state.formStage);
      if (idx > 0) state.formStage = DueDiligenceStageOrder[idx - 1];
    },
    toggleFormEditable(state, data: boolean) {
      state.isViewOnly = !data;
    },
    resetForm(state) {
      state.isViewOnly = true;
      state.openIntroductionModal = true;
      state.formStage = DueDiligenceStageOrder[0];
      state.isLoading = false;
      state.openConfirmationModal = false;
      state.clientBostonId = null;
      state.fiscalResidenceChanged = {
        PrimaryAccountHolder: { changed: false, previousCountry: null, newCountry: null },
        SecondaryAccountHolder: { changed: false, previousCountry: null, newCountry: null },
      };
    },
    addSourceItem(
      state,
      payload: { type: SourceType; item: DueDiligenceSourceOfWealth },
    ) {
      if (!state.details.SourceOfWealth.some((i) => i.Id === payload.item.Id)) {
        state.details.SourceOfWealth.push(payload.item);
      }

      if (!state.details.SourceOfFunds.some((i) => i.Id === payload.item.Id)) {
        state.details.SourceOfFunds.push(payload.item);
      }
    },
    removeSourceItem(state, payload: { type: SourceType; id: number }) {
      const wealthIndex = state.details.SourceOfWealth.findIndex(
        (i) => i.Id === payload.id,
      );
      if (wealthIndex !== -1) {
        state.details.SourceOfWealth.splice(wealthIndex, 1);
      }

      const fundsIndex = state.details.SourceOfFunds.findIndex(
        (i) => i.Id === payload.id,
      );
      if (fundsIndex !== -1) {
        state.details.SourceOfFunds.splice(fundsIndex, 1);
      }
    },
    setSourceItemRemarks(state, payload: { id: number; remarks: string }) {
      const { id, remarks } = payload;
      const updateIn = (arr: DueDiligenceSourceOfWealth[]) => {
        const idx = arr.findIndex(
          (i: DueDiligenceSourceOfWealth) => i.Id === id,
        );
        if (idx !== -1) {
          arr[idx].Remarks = remarks;
        }
      };

      updateIn(state.details.SourceOfWealth);
      updateIn(state.details.SourceOfFunds);
    },
    setMoreInformation(state, data: string) {
      state.details.MoreInformation = data;
    },
    setLoading(state, data: boolean) {
      state.isLoading = data;
    },
    setFiscalResidenceChanged(
      state,
      payload: { type: JointAccountType; previousCountry: number | null; newCountry: number | null },
    ) {
      state.fiscalResidenceChanged = {
        ...state.fiscalResidenceChanged,
        [payload.type]: {
          changed: payload.previousCountry !== payload.newCountry,
          previousCountry: payload.previousCountry,
          newCountry: payload.newCountry,
        },
      };
    },
  },
  actions: {
    async getUserReminder({ commit, rootState }, bostonId: number) {
      try {
        const response = await axios.get(endpoints.dueDiligenceReminder, {
          headers: { Authorization: `bearer ${rootState.account.token}` },
          params: {
            bostonId: bostonId,
          },
        });
        const { IsReminderDue, SkipCount, LastDueDiligenceUpdateDate } =
          response.data.Data;
        commit('setReminderData', {
          IsReminderDue: IsReminderDue,
          SkipCount: SkipCount,
          LastDueDiligenceUpdateDate: toDate(LastDueDiligenceUpdateDate),
        } as DueDiligenceReminder);
      } catch (err) {
        const msg = handleError(
          err,
          'Sorry! looks like something went wrong while loading your Due Diligence reminder',
        );
        this.dispatch('addError', msg);
      }
    },
    getOverviewModalCheck({ commit, rootState }, bostonId: number): boolean {
      const overviewModalCheck = localStorage.getItem(
        'OverviewModal-' + bostonId,
      );
      if (!overviewModalCheck) return true;

      const parsedCheck = JSON.parse(overviewModalCheck);

      const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
      const currentTime = Date.now();
      let isExpired = false;
      if (parsedCheck) {
        const timeElapsed = currentTime - parsedCheck;
        if (timeElapsed > TWENTY_FOUR_HOURS) {
          isExpired = true;
        }
      }
      return isExpired;
    },
    async skipUserReminder({ commit, rootState }, bostonId: number) {
      try {
        commit('setLoading', true);
        const url = endpoints.dueDiligenceSkip.replace(
          '{bostonId}',
          bostonId.toString(),
        );
        await axios.post(url, null, {
          headers: { Authorization: `bearer ${rootState.account.token}` },
        });
        commit('setLoading', false);
        commit('closeAllModals');
      } catch (err) {
        const msg = handleError(
          err,
          'Sorry! looks like something went wrong while skipping your Due Diligence reminder',
        );
        this.dispatch('addError', msg);
      }
    },
    toggleReminderModal({ commit }) {
      commit('toggleReminderModal');
    },
    toggleClientModal({ commit }) {
      commit('toggleClientModal');
    },
    toggleReminderOverviewModal({ commit }) {
      commit('toggleReminderOverviewModal');
    },
    toggleDocumentOverviewModal({ commit }) {
      commit('toggleDocumentOverviewModal');
      commit('setStage', 1);
    },
    toggleConfirmationModal({ commit }) {
      commit('toggleConfirmationModal');
    },
    toggleIntroductionModal({ commit }) {
      commit('toggleIntroductionModal');
    },
    closeAllModals({ commit }) {
      commit('closeAllModals');
    },
    async getUserDetails({ commit, rootState }, userinfoId: number) {
      try {
        const response = await axios.get(endpoints.dueDiligenceDetails, {
          headers: { Authorization: `bearer ${rootState.account.token}` },
          params: {
            userinfoId: userinfoId,
          },
        });

        const data = response.data.Data;

        const mapReason = (holder: any) => {
          const taxInfo = holder?.TaxInfo;
          if (!taxInfo) return;

          const option = AccountReasonNoTinOptions.find(
            (o) => o.value === taxInfo.ReasonNoTinNumber,
          );
          taxInfo.ReasonNoTinNumber = option ?? null;
        };

        mapReason(data?.GeneralDetails?.PrimaryAccountHolder);
        mapReason(data?.GeneralDetails?.SecondaryAccountHolder);

        commit('setDueDiligenceDetails', data);
      } catch (err) {
        const msg = handleError(
          err,
          'Sorry! looks like something went wrong while loading your Due Diligence details',
        );
        this.dispatch('addError', msg);
      }
    },
    async getDueDiligenceById(
      { commit, rootState, state },
      userinfoId: number,
    ) {
      const token = rootState.account.token;
      const url = endpoints.getDueDiligenceById + userinfoId.toString();

      try {
        const response = await axios.get(url, {
          headers: { Authorization: `bearer ${token}` },
        });

        const record: DueDiligenceRecord = response.data;
        commit('SetCurrentDueDiligenceDetails', record);
      } catch (err) {
        const msg = handleError(
          err,
          'Sorry! looks like something went wrong while loading your Due Diligence documents',
        );
        this.dispatch('addError', msg);
      }
    },
    async downloadDueDiligenceDocument({ rootState }, documentId: number) {
      try {
        const response = await axios.get(
          `${endpoints.downloadDueDiligenceDoc}/${documentId}`,
          {
            headers: { Authorization: `bearer ${rootState.account.token}` },
          },
        );

        const { FileName, FileType, Base64Data } = response.data;

        const byteCharacters = atob(Base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: FileType });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        console.log(link);
        link.href = url;

        link.setAttribute('download', FileName || `document_${documentId}`);

        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        const msg = handleError(
          err,
          'Sorry! looks like something went wrong while downloading your Due Diligence document',
        );
        this.dispatch('addError', msg);
      }
    },
    async getRequiredDocuments(
      { rootState },
      dueDiligenceId: number,
    ): Promise<DueDiligenceHolderSection[]> {
      const token = rootState.account.token;
      const url = endpoints.dueDiligenceRequiredDocuments.replace(
        '{id}',
        dueDiligenceId.toString(),
      );

      try {
        const response = await axios.get(url, {
          headers: { Authorization: `bearer ${token}` },
        });
        return response.data as DueDiligenceHolderSection[];
      } catch (err) {
        const msg = handleError(
          err,
          'Sorry! looks like something went wrong while loading your Due Diligence required documents',
        );
        this.dispatch('addError', msg);
        return [];
      }
    },
    async getDocumentTypes({ commit, rootState }, risk: number) {
      const token = rootState.account.token;
      const url = endpoints.getDocumentTypes + risk.toString();

      try {
        const response = await axios.get(url, {
          headers: { Authorization: `bearer ${token}` },
        });

        const record: DueDiligenceDocumentType[] = response.data;

        commit(
          'setDocumentTypes',
          record.sort((a, b) => a.RiskCategory - b.RiskCategory),
        );
      } catch (err) {
        const msg = handleError(
          err,
          'Sorry! looks like something went wrong while loading your Due Diligence document types',
        );
        this.dispatch('addError', msg);
      }
    },
    async getClients({ rootState, commit }) {
      const token = rootState.account.token;
      const companyId = rootState.account.details.whiteLabelId;
      const bostonId = rootState.account.details.bostonId;
      const roles = rootState.account.details.role;

      commit('setLoading', true);
      try {
        if (
          !roles.includes(USER_ROLES.CONSULTANT) &&
          !roles.includes(USER_ROLES.WHITELABEL_ADMIN)
        )
          return;

        let url = endpoints.dueDiligenceAdminClients.replace(
          '{whitelabelId}',
          companyId,
        );

        if (
          roles.includes(USER_ROLES.CONSULTANT) &&
          !roles.includes(USER_ROLES.WHITELABEL_ADMIN)
        ) {
          url = endpoints.dueDiligenceClients
            .replace('{whitelabelId}', companyId)
            .replace('{consultantId}', bostonId);
        }

        const response = await axios.get(url, {
          headers: { Authorization: `bearer ${token}` },
        });

        commit(
          'setClients',
          response.data.map((item: any): DueDiligenceClient => {
            return {
              userInfoId: item.UserInfoId,
              name: item.Name,
              accountNumber: item.Account,
              moventumNo: item.MoventumNo,
              riskCategory: item.RiskCategory,
              aum: item.Aum,
              dueDate: item.DueDate,
              status: setStatusText(item.Status),
              statusId: item.Status,
              daysOverdue: item.OverDueDays ?? 0,
              lastSubmission: item.LastSubmission,
              dueDiligenceId: item.DueDiligenceId,
              bostonId: item.BostonId,
            };
          }),
        );
      } catch (err) {
        const msg = handleError(
          err,
          'Sorry! looks like something went wrong while loading your Due Diligence clients',
        );
        this.dispatch('addError', msg);
      } finally {
        commit('setLoading', false);
      }
    },
    async uploadDueDiligenceDocument(
      { commit, rootState },
      formData: FormData,
    ) {
      try {
        await axios.put(endpoints.uploadDueDiligenceDoc, formData, {
          headers: {
            Authorization: `bearer ${rootState.account.token}`,
          },
        });
      } catch (err) {
        const msg = handleError(
          err,
          'Sorry! looks like something went wrong while uploading your Due Diligence document',
        );
        this.dispatch('addError', msg);
      }
    },
    async updateUserDetails(
      { commit, rootState },
      details: DueDiligenceDetails,
    ) {
      try {
        commit('setLoading', true);
        const response = await axios.post(
          endpoints.dueDiligenceDetails,
          withNormalizedTaxReasons(details),
          { headers: { Authorization: `bearer ${rootState.account.token}` } },
        );

        commit('toggleConfirmationModal');
        commit('setLoading', false);
      } catch (err) {
        const msg = handleError(
          err,
          'Sorry! looks like something went wrong while loading your Due Diligence metadata',
        );
        this.dispatch('addError', msg);
      }
    },
    async getMetadata({ commit, rootState }) {
      try {
        const response = await axios.get(endpoints.dueDiligenceMetadata, {
          headers: { Authorization: `bearer ${rootState.account.token}` },
        });
        commit('setDueDiligenceMetadata', response.data.Data);
      } catch (err) {
        const msg = handleError(
          err,
          'Sorry! looks like something went wrong while loading your Due Diligence metadata',
        );
        this.dispatch('addError', msg);
      }
    },
    async getDueDiligenceList({ commit, rootState }) {
      const whiteLabelId = rootState.account.details.whiteLabelId;

      try {
        const response = await axios.get(
          endpoints.dueDiligenceAdminClients.replace(
            '{whitelabelId}',
            whiteLabelId,
          ),
          {
            headers: {
              Authorization: `bearer ${rootState.account.token}`,
            },
          },
        );

        commit('setDueDiligenceList', response.data);
        return response.data;
      } catch (err) {
        const msg = handleError(
          err,
          'Sorry! Looks like something went wrong while trying to access the due diligence list',
        );
        this.dispatch('addError', msg, { root: true });
      }
    },
    setStage({ commit }, stage: DueDiligenceFormStage) {
      commit('setFormStage', stage);
    },
    setDocumentTypes({ commit }, stage: DueDiligenceDocumentType[]) {
      commit('setDocumentTypes', stage);
    },
    SetCurrentDueDiligenceDetails({ commit }, stage: DueDiligenceRecord) {
      commit('SetCurrentDueDiligenceDetails', stage);
    },
    goNext({ commit }) {
      commit('nextStage');
    },
    goPrev({ commit }) {
      commit('prevStage');
    },
  },
};
